// Shared admin data table (ADM-UX-1, Part A).
//
// react-table v7 stays the column/row engine and this component stays
// presentation-only: consumers keep their own server-side pagination,
// filtering and row actions, and pass in already-paginated rows.
//
// Presentation direction: white card surface, subtle row dividers instead of
// boxed cells, sticky header, hover/selected affordances and a scroll
// container so wide operational datasets never break the page layout.
import { Box, Flex, Skeleton, Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/react";
import NoData from "components/NoData";
import Paginator from "components/Paginator";
import { type KeyboardEvent, type MouseEvent, useEffect, useMemo, useState } from "react";
import { useTable, type Row } from "react-table";
import type { AdminDataTableProps } from "./AdminDataTable.types";

const SURFACE_BORDER = "#E2E8F0";
const ROW_DIVIDER = "#EDF2F7";
const HEADER_BG = "#F7FAFC";
const ROW_HOVER_BG = "#F5F9FF";
const TEXT_PRIMARY = "#2b3b4c";
const TEXT_MUTED = "#7f94a6";

const SKELETON_ROW_COUNT = 6;

// Default rows per page for the opt-in client-side pagination mode.
const DEFAULT_PAGE_SIZE = 10;

// Clicking a control inside a selectable row must keep working, so row
// selection ignores any click that starts on an interactive element.
const INTERACTIVE_TARGET_SELECTOR =
  "button, a, input, select, textarea, [role='button'], [data-row-click-ignore]";

const isInteractiveTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(INTERACTIVE_TARGET_SELECTOR) !== null;

const headerCellProps = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  bg: HEADER_BG,
  color: TEXT_MUTED,
  fontSize: "xs",
  fontWeight: "semibold",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
  px: 4,
  py: 3,
  borderBottomWidth: "1px",
  borderBottomColor: SURFACE_BORDER,
} as const;

const bodyCellSx = {
  "& > td": { borderBottomWidth: "1px", borderBottomColor: ROW_DIVIDER },
  "&:last-of-type > td": { borderBottomWidth: 0 },
} as const;

const SkeletonRow = ({ columnCount }: { columnCount: number }) => (
  <Tr sx={bodyCellSx}>
    {Array.from({ length: columnCount }, (_, cellIndex) => (
      <Td key={cellIndex} px={4} py={3}>
        <Skeleton height="14px" borderRadius="4px" w="100%" />
      </Td>
    ))}
  </Tr>
);

const AdminDataTable = <TData extends object>({
  data,
  columns: columnDefinitions,
  loading = false,
  selectedRow,
  showRowNumbers = false,
  startIndex = 1,
  getRowId,
  paginate = false,
  initialPageSize = DEFAULT_PAGE_SIZE,
  maxBodyHeight = "640px",
  emptyState,
  ariaLabel,
}: AdminDataTableProps<TData>) => {
  const columns = useMemo(() => columnDefinitions, [columnDefinitions]);
  const rowsData = useMemo(() => data ?? [], [data]);
  const isSelectable = selectedRow !== undefined;

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const pageCount = Math.max(1, Math.ceil(rowsData.length / pageSize));
  const activePageIndex = Math.min(pageIndex, pageCount - 1);

  // Persist the clamp: when the rows shrink (e.g. a search filter narrows the
  // list) the stored page is pulled back into range, so re-expanding the list
  // does not jump back to a page that no longer fits.
  useEffect(() => {
    if (pageIndex !== activePageIndex) setPageIndex(activePageIndex);
  }, [pageIndex, activePageIndex]);

  const rowNumberOffset = paginate ? activePageIndex * pageSize : 0;

  const tableOptions = useMemo(
    () => ({
      columns,
      data: rowsData,
      getRowId: getRowId === undefined ? undefined : (row: TData): string => getRowId(row),
    }),
    [columns, rowsData, getRowId],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<TData>(tableOptions);

  // Client-side pagination exists for fully-loaded lists; server-paged pages
  // keep owning their own paging and pass the page's rows unchanged.
  const visibleRows = paginate
    ? rows.slice(activePageIndex * pageSize, (activePageIndex + 1) * pageSize)
    : rows;

  const columnCount = columns.length + (showRowNumbers ? 1 : 0);

  const handleRowClick =
    (row: Row<TData>) =>
    (event: MouseEvent<HTMLTableRowElement>): void => {
      if (selectedRow === undefined || isInteractiveTarget(event.target)) return;
      selectedRow(row.original);
    };

  const handleRowKeyDown =
    (row: Row<TData>) =>
    (event: KeyboardEvent<HTMLTableRowElement>): void => {
      if (selectedRow === undefined || event.target !== event.currentTarget) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      selectedRow(row.original);
    };

  return (
    <Box
      bg="white"
      borderWidth="1px"
      borderColor={SURFACE_BORDER}
      borderRadius="12px"
      overflow="hidden"
    >
      <Box
        data-testid="admin-data-table-scroll"
        overflowX="auto"
        overflowY="auto"
        maxH={maxBodyHeight}
      >
        <Table
          {...getTableProps()}
          aria-label={ariaLabel}
          variant="unstyled"
          size="sm"
          sx={{ borderCollapse: "separate", borderSpacing: 0, width: "100%" }}
        >
          <Thead>
            {headerGroups.map((headerGroup, headerGroupIndex) => (
              <Tr key={headerGroupIndex} {...headerGroup.getHeaderGroupProps()}>
                {showRowNumbers ? (
                  <Th {...headerCellProps} w="56px">
                    S/N
                  </Th>
                ) : null}
                {headerGroup.headers.map((column) => (
                  <Th
                    key={column.id}
                    {...column.getHeaderProps()}
                    {...headerCellProps}
                    minWidth={column.minWidth || undefined}
                    textAlign={column.align ?? "left"}
                  >
                    {column.render("Header")}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          {loading ? (
            <Tbody>
              {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
                <SkeletonRow key={rowIndex} columnCount={columnCount} />
              ))}
            </Tbody>
          ) : (
            <Tbody {...getTableBodyProps()}>
              {visibleRows.length === 0 ? (
                <Tr sx={bodyCellSx}>
                  <Td colSpan={columnCount} borderBottomWidth={0} py={12}>
                    <Flex align="center" justify="center">
                      {emptyState ?? <NoData />}
                    </Flex>
                  </Td>
                </Tr>
              ) : (
                visibleRows.map((row: Row<TData>, rowIndex: number) => {
                  prepareRow(row);
                  return (
                    <Tr
                      key={row.id}
                      {...row.getRowProps()}
                      bg="white"
                      color={TEXT_PRIMARY}
                      cursor={isSelectable ? "pointer" : "default"}
                      tabIndex={isSelectable ? 0 : undefined}
                      _hover={{ bg: ROW_HOVER_BG }}
                      _focusVisible={{
                        outline: "2px solid",
                        outlineColor: "primary",
                        outlineOffset: "-2px",
                      }}
                      onClick={handleRowClick(row)}
                      onKeyDown={handleRowKeyDown(row)}
                      sx={bodyCellSx}
                    >
                      {showRowNumbers ? (
                        <Td
                          key="row-number"
                          color={TEXT_MUTED}
                          fontSize="xs"
                          px={4}
                          py={3}
                          verticalAlign="middle"
                        >
                          {startIndex + rowNumberOffset + rowIndex}
                        </Td>
                      ) : null}
                      {row.cells.map((cell) => (
                        <Td
                          key={cell.column.id}
                          {...cell.getCellProps()}
                          minWidth={cell.column.minWidth || undefined}
                          textAlign={cell.column.align ?? "left"}
                          fontSize="sm"
                          px={4}
                          py={3}
                          verticalAlign="middle"
                        >
                          {cell.render("Cell")}
                        </Td>
                      ))}
                    </Tr>
                  );
                })
              )}
            </Tbody>
          )}
        </Table>
      </Box>

      {paginate && rowsData.length > 0 ? (
        <Box borderTopWidth="1px" borderColor={SURFACE_BORDER} px={4}>
          <Paginator
            pageCount={pageCount}
            pageNo={activePageIndex}
            pageSize={pageSize}
            onPageChange={(page) => {
              setPageIndex(page.selected);
            }}
            onSizeChange={(size) => {
              setPageSize(Number(size));
              setPageIndex(0);
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default AdminDataTable;
